import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Play, X, Phone, MessageSquare, Mail, BarChart3, Bot, Workflow, CheckCircle, Volume2 } from 'lucide-react';
import ConversationDemo from './ConversationDemo';

const DemoModal = ({ open, onOpenChange }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [demoType, setDemoType] = useState('conversation');

  const demoSteps = [
    {
      title: 'Welcome to PropFlo',
      description: 'Your AI-powered lead qualification platform that works 24/7 to capture, qualify, and prioritize real estate leads.',
      icon: Bot,
      features: [
        'Never miss a lead - even at 2 AM',
        'Automated qualification via Voice, SMS & Email',
        'Smart lead scoring with AI',
        'Instant response in under 60 seconds'
      ]
    },
    {
      title: 'Dashboard Overview',
      description: 'Get real-time insights into your lead pipeline with comprehensive analytics and status tracking.',
      icon: BarChart3,
      features: [
        'Track Hot, Warm, and Cold leads',
        'Monitor conversion rates',
        'View agent activity metrics',
        'Integration status at a glance'
      ]
    },
    {
      title: 'Lead Management',
      description: 'Efficiently manage all your leads in one place with powerful organization and contact tools.',
      icon: Phone,
      features: [
        'Create and organize leads',
        'Add notes and timeline tracking',
        'One-click call, SMS, or email',
        'Automatic lead classification'
      ]
    },
    {
      title: 'AI Agents',
      description: 'Configure intelligent AI agents for voice calls, SMS, and email with customized scripts for your market.',
      icon: MessageSquare,
      features: [
        'Voice Agent - AI-powered phone calls',
        'SMS Agent - Instant text responses',
        'Email Agent - Professional automation',
        'Pre-built templates for Saint John, NB'
      ]
    },
    {
      title: 'Workflow Builder',
      description: 'Design conversation flows that guide leads through qualification with proven real estate scripts.',
      icon: Workflow,
      features: [
        'Buyer qualification workflows',
        'Seller qualification flows',
        'Investment property scripts',
        'Visual node-based editor'
      ]
    },
    {
      title: 'Integrations',
      description: 'Connect with your existing tools for seamless workflow automation and CRM synchronization.',
      icon: Mail,
      features: [
        'Twilio for Voice & SMS',
        'Make.com for automation',
        'Follow Up Boss CRM sync',
        'OpenAI GPT-5 powered responses'
      ]
    }
  ];

  const currentDemo = demoSteps[currentStep];
  const Icon = currentDemo.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl bg-gray-900 border-gray-800 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold">Product Demo</DialogTitle>
            <button
              onClick={() => onOpenChange(false)}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Demo Type Selector */}
          <Tabs value={demoType} onValueChange={setDemoType} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-gray-800">
              <TabsTrigger value="conversation" className="data-[state=active]:bg-blue-600">
                <Phone className="w-4 h-4 mr-2" />
                Live AI Demo
              </TabsTrigger>
              <TabsTrigger value="tour" className="data-[state=active]:bg-blue-600">
                <BarChart3 className="w-4 h-4 mr-2" />
                Feature Tour
              </TabsTrigger>
            </TabsList>

            {/* Live Conversation Demo */}
            <TabsContent value="conversation" className="space-y-6">
              <Card className="bg-gray-800/50 border-gray-700 p-6">
                <h3 className="text-xl font-bold text-white mb-4">
                  See Jason AI Qualify Real Estate Leads in Real-Time
                </h3>
                <p className="text-gray-400 mb-6">
                  Watch how our AI-powered agent conducts voice calls, SMS conversations, and email exchanges to qualify leads and sync with Follow Up Boss CRM.
                </p>

                <Tabs defaultValue="voice" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 bg-gray-900">
                    <TabsTrigger value="voice">
                      <Phone className="w-4 h-4 mr-2" />
                      Voice Call
                    </TabsTrigger>
                    <TabsTrigger value="sms">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      SMS
                    </TabsTrigger>
                    <TabsTrigger value="email">
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="voice" className="mt-4">
                    {/* Real Call Recording Section */}
                    <Card className="bg-gray-900 border-gray-700 p-6 mb-4">
                      <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <Volume2 className="w-5 h-5 text-blue-400" />
                        Real Call Recording
                      </h4>
                      <p className="text-gray-400 text-sm mb-4">
                        Listen to an actual AI-powered call conversation
                      </p>
                      <audio controls className="w-full" style={{ filter: 'invert(1) hue-rotate(180deg)' }}>
                        <source src="/demo-recording.wav" type="audio/wav" />
                        Your browser does not support the audio element.
                      </audio>
                    </Card>
                    
                    {/* Interactive Demo */}
                    <ConversationDemo type="voice" />
                  </TabsContent>

                  <TabsContent value="sms" className="mt-4">
                    <ConversationDemo type="sms" />
                  </TabsContent>

                  <TabsContent value="email" className="mt-4">
                    <ConversationDemo type="email" />
                  </TabsContent>
                </Tabs>
              </Card>

              {/* Integration Highlight */}
              <Card className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-700 p-6">
                <h4 className="text-lg font-bold text-white mb-4">Powered By:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Phone className="w-6 h-6" />
                    </div>
                    <p className="text-sm text-gray-300 font-semibold">Twilio</p>
                    <p className="text-xs text-gray-500">Voice & SMS</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Bot className="w-6 h-6" />
                    </div>
                    <p className="text-sm text-gray-300 font-semibold">OpenAI GPT-5</p>
                    <p className="text-xs text-gray-500">AI Brain</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Workflow className="w-6 h-6" />
                    </div>
                    <p className="text-sm text-gray-300 font-semibold">Follow Up Boss</p>
                    <p className="text-xs text-gray-500">CRM Sync</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Mail className="w-6 h-6" />
                    </div>
                    <p className="text-sm text-gray-300 font-semibold">Make.com</p>
                    <p className="text-xs text-gray-500">Automation</p>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Feature Tour */}
            <TabsContent value="tour" className="space-y-6">
              {/* Interactive Feature Walkthrough */}
              <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                <h3 className="text-xl font-bold mb-4 text-center text-white">Interactive Feature Tour</h3>
                
                {/* Progress Indicator */}
                <div className="flex justify-center gap-2 mb-6">
                  {demoSteps.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentStep(index)}
                      className={`h-2 rounded-full transition-all ${
                        index === currentStep
                          ? 'w-8 bg-blue-500'
                          : index < currentStep
                          ? 'w-2 bg-blue-700'
                          : 'w-2 bg-gray-600'
                      }`}
                    />
                  ))}
                </div>

                {/* Current Step Content */}
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold mb-2">{currentDemo.title}</h4>
                      <p className="text-gray-400 text-lg">{currentDemo.description}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    {currentDemo.features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-3 bg-gray-900/50 rounded-lg border border-gray-700"
                      >
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8">
                  <Button
                    onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                    disabled={currentStep === 0}
                    variant="outline"
                    className="border-gray-700 hover:bg-gray-800"
                  >
                    Previous
                  </Button>
                  <span className="text-gray-400">
                    Step {currentStep + 1} of {demoSteps.length}
                  </span>
                  {currentStep < demoSteps.length - 1 ? (
                    <Button
                      onClick={() => setCurrentStep(currentStep + 1)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      onClick={() => onOpenChange(false)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Start Free Trial
                    </Button>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-gray-800/50 border-gray-700 p-4 text-center">
              <div className="text-3xl font-bold text-blue-400 mb-1">2,500+</div>
              <div className="text-sm text-gray-400">Active Realtors</div>
            </Card>
            <Card className="bg-gray-800/50 border-gray-700 p-4 text-center">
              <div className="text-3xl font-bold text-green-400 mb-1">500K+</div>
              <div className="text-sm text-gray-400">Leads Qualified</div>
            </Card>
            <Card className="bg-gray-800/50 border-gray-700 p-4 text-center">
              <div className="text-3xl font-bold text-purple-400 mb-1">40%</div>
              <div className="text-sm text-gray-400">Avg. Conversion</div>
            </Card>
            <Card className="bg-gray-800/50 border-gray-700 p-4 text-center">
              <div className="text-3xl font-bold text-orange-400 mb-1">24/7</div>
              <div className="text-sm text-gray-400">Always Active</div>
            </Card>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-center">
            <h4 className="text-2xl font-bold mb-2">Ready to Transform Your Lead Qualification?</h4>
            <p className="text-blue-100 mb-4">Join thousands of realtors who never miss a qualified lead</p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => {
                  onOpenChange(false);
                  window.location.href = '/login';
                }}
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                Start Free Trial
              </Button>
              <Button
                onClick={() => setCurrentStep(0)}
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                Restart Tour
              </Button>
            </div>
            <p className="text-sm text-blue-100 mt-4">
              14-Day Free Trial • No Credit Card Required • Setup in 10 Minutes
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DemoModal;
